import { cn } from "@/lib/utils";
import type { DashboardLabels, DashboardPeriod } from "./types";
import { DashboardPeriodSwitcher } from "./DashboardPeriodSwitcher";
import { freshnessLabel, greetingSubtitle } from "./utils";

export interface DashboardHeroProps {
  organizationName: string;
  branchName: string;
  updatedAtIso: string;
  now?: Date;
  period: DashboardPeriod;
  onPeriodChange: (period: DashboardPeriod) => void;
  labels: DashboardLabels;
  className?: string;
}

export function DashboardHero({
  organizationName,
  branchName,
  updatedAtIso,
  now,
  period,
  onPeriodChange,
  labels,
  className,
}: DashboardHeroProps) {
  const currentNow = now ?? new Date();
  const subtitle = greetingSubtitle(currentNow, labels.page);
  const fresh = freshnessLabel(updatedAtIso, currentNow, labels.page);
  const stalenessClass =
    fresh.staleness === "very-stale"
      ? "text-biz-warning"
      : "text-biz-foreground-muted";

  return (
    <header
      className={cn(
        "flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between",
        className,
      )}
    >
      <div className="min-w-0">
        <p className="text-xs font-medium uppercase tracking-wider text-biz-foreground-muted">
          {organizationName} · {branchName}
        </p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-biz-foreground sm:text-[28px]">
          {labels.page.welcomeTitle}
        </h1>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-biz-foreground-muted">
          <span>{subtitle}</span>
          <span aria-hidden className="hidden text-biz-foreground-subtle sm:inline">
            ·
          </span>
          <span className={cn("text-xs", stalenessClass)} aria-live="polite">
            {fresh.text}
          </span>
        </div>
      </div>
      <DashboardPeriodSwitcher
        period={period}
        onPeriodChange={onPeriodChange}
        labels={labels.periods}
      />
    </header>
  );
}
